"use client";
import React, { useEffect, useState, useMemo } from "react";
import { marked } from "marked";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TurndownService from "turndown";
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { DOMParser as ProseMirrorDOMParser } from 'prosemirror-model';

// Listas explícitas
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";

// Alineación
import TextAlign from "@tiptap/extension-text-align";

// Iconos
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List as ListIcon, ListOrdered, AlignLeft, AlignCenter, AlignRight,
} from "lucide-react";

const turndown = new TurndownService({
  headingStyle: "atx",
  codeBlockStyle: "fenced",
});

const MarkdownPaste = Extension.create({
  name: 'markdownPaste',
  
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('markdownPaste'),
        props: {
          handlePaste(view, event) {
            const text = event.clipboardData?.getData('text/plain');
            if (!text) return false;
            
            const hasMarkdownPatterns = /^#{1,6}\s|^\*\*|^__|\*[^*]+\*|_[^_]+_|^\d+\.|^[-*+]\s|^\[.+\]\(.+\)|^>.+|^```/m.test(text);
            
            if (hasMarkdownPatterns) {
              event.preventDefault();
              const html = marked.parse(text);
              
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = html;
              
              const parser = ProseMirrorDOMParser.fromSchema(view.state.schema);
              const slice = parser.parseSlice(tempDiv);
              const tr = view.state.tr.replaceSelection(slice);
              view.dispatch(tr);
              
              return true;
            }
            
            return false;
          }
        }
      })
    ];
  }
});


export default function MarkdownToolbarEditor({
  value = "",
  onChange,                               // ({ html, markdown })
  placeholder = "Write here...",
  editorMinHeight = "min-h-[520px]",
  className = "",                         // usa "relative" si quieres la toolbar flotante
  toolbarClassName = "",                  // posición absoluta de la toolbar
}) {
  // Si value está vacío, inicia en edición (editMode=true). Si tiene contenido, inicia en preview (editMode=false)
  const [editMode, setEditMode] = useState(() => (value && value.trim() !== "") ? false : true);
  const [, setTick] = useState(0);

  // Detecta si el valor es markdown (no contiene tags HTML principales)
  const isProbablyMarkdown = useMemo(() => {
    if (!value) return true;
    // Si contiene tags HTML principales, lo tratamos como HTML
    return !/<\s*(p|ul|ol|li|h1|h2|h3|h4|h5|h6|strong|em|b|i|blockquote|pre|code)[^>]*>/i.test(value);
  }, [value]);

  // Convierte markdown a HTML si es necesario
  const initialHtml = useMemo(() => {
    if (!value) return "<p></p>";
    return isProbablyMarkdown ? marked.parse(value) : value;
  }, [value, isProbablyMarkdown]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ 
        bulletList: false, 
        orderedList: false, 
        listItem: false,
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        bold: {
          HTMLAttributes: {
            class: 'font-semibold',
          },
        },
        italic: {
          HTMLAttributes: {
            class: 'italic',
          },
        },
        strike: {
          HTMLAttributes: {
            class: 'line-through',
          },
        },
        code: {
          HTMLAttributes: {
            class: 'bg-purple-900/20 px-1.5 py-0.5 rounded text-purple-300',
          },
        },
        codeBlock: {
          HTMLAttributes: {
            class: 'bg-black/40 border border-white/10 rounded p-4',
          },
        },
        blockquote: {
          HTMLAttributes: {
            class: 'border-l-4 border-purple-500 pl-4 italic text-gray-400',
          },
        },
      }),
      Underline,
      MarkdownPaste,
      BulletList.configure({
        keepMarks: true,
        keepAttributes: true,
        HTMLAttributes: { class: "list-disc pl-6 space-y-1" },
      }),
      OrderedList.configure({
        keepMarks: true,
        keepAttributes: true,
        HTMLAttributes: { class: "list-decimal pl-6 space-y-1" },
      }),
      ListItem,
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center", "right"],
        defaultAlignment: "left",
      }),
    ],
    content: initialHtml,
    editorProps: {
      attributes: {
        class:
          "prose prose-invert max-w-none focus:outline-none text-white " +
          "prose-p:my-2 prose-ol:my-2 prose-ul:my-2",
      },
    },
    onUpdate({ editor }) {
      const html = editor.getHTML();
      const markdown = turndown.turndown(html);
      onChange?.({ html, markdown });
    },
  });

  useEffect(() => {
    if (!editor) return;
    const rerender = () => setTick((x) => x + 1);
    editor.on("selectionUpdate", rerender);
    editor.on("transaction", rerender);
    editor.on("update", rerender);
    return () => {
      editor.off("selectionUpdate", rerender);
      editor.off("transaction", rerender);
      editor.off("update", rerender);
    };
  }, [editor]);

  // Solo actualiza el contenido si el HTML externo cambia
  useEffect(() => {
    if (!editor) return;
    const currentHtml = editor.getHTML();
    if (isProbablyMarkdown) {
      // Si el valor es markdown, conviértelo a HTML antes de setearlo
      const html = marked.parse(value || "");
      if (currentHtml !== html) {
        editor.commands.setContent(html || "<p></p>");
      }
    } else {
      if (currentHtml !== value) {
        editor.commands.setContent(value || "<p></p>");
      }
    }
  }, [editor, value, isProbablyMarkdown]);

  // Toggle entre edición y solo visualización
  const handleToggle = () => setEditMode((m) => !m);

  const btn = "w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-white/10 text-slate-200";
  const Btn = ({ active, onClick, aria, children }) => (
    <button
      type="button"
      aria-label={aria}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`${btn} ${active ? "bg-white/10" : ""}`}
    >
      {children}
    </button>
  );

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-2">
        <button
          className="px-3 py-1 rounded bg-slate-700 text-white text-xs hover:bg-slate-600"
          onClick={handleToggle}
        >
          {editMode ? "Preview as user" : "Edit markdown"}
        </button>
      </div>
      {editMode ? (
        <>
          <div className={`flex items-center gap-1 px-2 py-1 bg-[#2c2c32] rounded-xl shadow-[0_2px_12px_rgba(0,0,0,.25)] ${toolbarClassName}`}>
            <Btn aria="Bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}><Bold size={18} /></Btn>
            <Btn aria="Italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}><Italic size={18} /></Btn>
            <Btn aria="Underline" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")}><UnderlineIcon size={18} /></Btn>
            <Btn aria="Strike" onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")}><Strikethrough size={18} /></Btn>
            <div className="mx-2 h-5 w-px bg-white/10" />
            <Btn aria="Bullet list" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}><ListIcon size={18} /></Btn>
            <Btn aria="Ordered list" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}><ListOrdered size={18} /></Btn>
            <div className="mx-2 h-5 w-px bg-white/10" />
            <Btn aria="Align left" onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })}><AlignLeft size={18} /></Btn>
            <Btn aria="Align center" onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })}><AlignCenter size={18} /></Btn>
            <Btn aria="Align right" onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })}><AlignRight size={18} /></Btn>
          </div>
          <div className={`rounded-[18px] border border-white/10 bg-[#1F1F1F80] px-4 py-3 ${editorMinHeight} overflow-auto`}>
            {editor.isEmpty && (
              <div className="pointer-events-none text-slate-500 select-none">
                {placeholder}
              </div>
            )}
            <EditorContent editor={editor} />
          </div>
        </>
      ) : (
        <div className={`rounded-[18px] border border-white/10 bg-[#1F1F1F80] px-4 py-3 ${editorMinHeight} overflow-auto`}>
          {/* Renderiza el HTML generado desde markdown si es necesario */}
          <div className="prose prose-invert max-w-none text-white" dangerouslySetInnerHTML={{ __html: initialHtml }} />
        </div>
      )}
    </div>
  );
}
